import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useLogin } from "../../context/LoginProvider";
// import { useSocket } from "../../context/SocketProvider"; // Removed - using Web Speech API instead
import Select from 'react-select';
import { countries } from "../../utils/countries";
import { motion, AnimatePresence } from "framer-motion";
import AlertBox from "../../components/AlertBox/AlertBox";
import AnimatedBackground from "../../components/AnimatedBackground/AnimatedBackground";
import Navbar from "../../components/Navbar";
import cupidImage from "../../assets/cupid.png";
import loadingGif from "../../assets/loadingGif.gif";
import solEnd from "../../assets/sol-end.mp3";
import "./GuestInfoForm.css";

const getQuestions = (userName) => [
  {
    id: "name",
    question: "Welcome! I'm Sol, your matchmaking assistant. What name would you like me to use for you?",
    placeholder: "Enter your name",
    type: "text",
    tip: "Perfect! I'll use this name to personalize your experience! 🌟",
    supportsVoice: true,
    icon: "👋"
  },
  {
    id: "age", 
    question: userName ? `Nice to meet you, ${userName}! What's your age so I can find suitable matches?` : "What's your age so I can find suitable matches?",
    placeholder: "Your age",
    type: "number",
    min: 13,
    max: 120,
    tip: "Great! This helps me connect you with people in your age range! ✨",
    supportsVoice: true,
    icon: "🎂"
  },
  {
    id: "country",
    question: userName ? `${userName}, which country do you live in? This helps with timezone matching!` : "Which country do you live in? This helps with timezone matching!",
    placeholder: "Select your country",
    type: "select",
    tip: "Excellent! Now I can find people both locally and globally! 🌍",
    supportsVoice: true,
    icon: "🌍"
  },
  {
    id: "relationshipType",
    question: userName ? `${userName}, what kind of connection would make you happiest right now?` : "What kind of connection would make you happiest right now?",
    placeholder: "Choose connection type",
    type: "select",
    tip: "Perfect choice! This helps me find people with similar relationship goals! 💕",
    supportsVoice: true,
    icon: "💫"
  },
  {
    id: "interests",
    question: userName ? `Last question, ${userName}! Share your passions - what activities make you feel most alive?` : "Last question! Share your passions - what activities make you feel most alive?",
    placeholder: "Describe your interests and hobbies... (e.g., hiking, photography, cooking, reading, gaming, art...)",
    type: "textarea",
    tip: "Amazing! The more you share, the better I can match you with someone who shares your enthusiasm! 🎯",
    supportsVoice: true,
    icon: "🎨"
  }
];

const AVATAR_STATES = {
  GREETING: "greeting",
  LISTENING: "listening", 
  THINKING: "thinking",
  HAPPY: "happy",
  WAITING: "waiting",
  TALKING: "talking"
};

function GuestInfoForm() {
  const { guestData, setGuestUserData } = useLogin();
  // const { socket, audioSocket } = useSocket(); // Removed - using Web Speech API instead
  const navigate = useNavigate();
  
  // Form state
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answeredQuestions, setAnsweredQuestions] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    age: "",
    country: "",
    relationshipType: "",
    interests: "",
  });
  
  // UI state
  const [avatarState, setAvatarState] = useState(AVATAR_STATES.GREETING);
  const [isTyping, setIsTyping] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTip, setShowTip] = useState(false);
  const [displayedQuestion, setDisplayedQuestion] = useState("");
  const [userName, setUserName] = useState("");
  const [isSpeechEnabled, setIsSpeechEnabled] = useState(true);
  const [isCompleted, setIsCompleted] = useState(false);
  
  // Voice input state - simplified
  const [isRecording, setIsRecording] = useState(false);
  const [currentRecordingField, setCurrentRecordingField] = useState(null);
  
  // Voice recognition refs
  const recognitionRef = useRef(null);
  const isListeningRef = useRef(false);
  const currentRecordingFieldRef = useRef(null); // Use ref to avoid state timing issues
  
  // Refs
  const inputRef = useRef(null);
  const avatarRef = useRef(null);
  const endAudioRef = useRef(null);
  const speechSynthRef = useRef(null);
  
  // Options for selects
  const countryOptions = countries.map(country => ({
    value: country.name,
    label: country.name,
    code: country.code
  }));

  const relationshipOptions = [
    { value: "friendship", label: "Friendship" },
    { value: "dating", label: "Dating" },
    { value: "serious_relationship", label: "Serious Relationship" },
    { value: "casual", label: "Casual" },
    { value: "networking", label: "Networking" },
    { value: "any", label: "Open to Anything" }
  ];

  // Get relationship type label for display
  const getRelationshipTypeLabel = (type) => {
    const option = relationshipOptions.find(opt => opt.value === type);
    return option ? option.label : type;
  };

  // Get country label for display
  const getCountryLabel = (countryName) => {
    const option = countryOptions.find(opt => opt.value === countryName);
    return option ? option.label : countryName;
  };

  // Speech synthesis setup
  useEffect(() => {
    // Check if speech synthesis is available
    if ('speechSynthesis' in window) {
      speechSynthRef.current = window.speechSynthesis;
    }
  }, []);

  // Initialize Speech Recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-US';
      
      recognitionRef.current.onstart = () => {
        console.log('Speech recognition started');
        setIsRecording(true);
        setAvatarState(AVATAR_STATES.LISTENING);
      };
      
      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        console.log('Speech recognition result:', transcript);
        
        // Process the result immediately with the current field from ref
        const fieldId = currentRecordingFieldRef.current;
        console.log('Current recording field ID from ref:', fieldId);
        console.log('Current recording field state:', currentRecordingField);
        if (fieldId) {
          console.log('Calling handleVoiceResult with:', transcript, fieldId);
          handleVoiceResult(transcript, fieldId);
        } else {
          console.log('No fieldId available in ref, cannot process voice result');
        }
      };
      
      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsRecording(false);
        setCurrentRecordingField(null);
        currentRecordingFieldRef.current = null;
        setAvatarState(AVATAR_STATES.WAITING);
        
        if (event.error !== 'no-speech') {
          setAlertMessage('Voice recognition failed. Please try again.');
          setShowAlert(true);
        }
      };
      
      recognitionRef.current.onend = () => {
        console.log('Speech recognition ended');
        setIsRecording(false);
        setAvatarState(AVATAR_STATES.THINKING);
        
        setTimeout(() => {
          setAvatarState(AVATAR_STATES.WAITING);
          setCurrentRecordingField(null);
          currentRecordingFieldRef.current = null;
        }, 1000);
      };
    } else {
      console.log('Speech recognition not supported');
    }
  }, []);



  // Cleanup effect for speech resources
  useEffect(() => {
    return () => {
      // Cancel any ongoing speech
      if (speechSynthRef.current && speechSynthRef.current.speaking) {
        speechSynthRef.current.cancel();
      }
      // Stop speech recognition
      if (recognitionRef.current && isListeningRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  // Handle voice recognition result
  const handleVoiceResult = (transcript, fieldId) => {
    if (!fieldId) {
      console.log('No fieldId provided to handleVoiceResult');
      return;
    }
    
    console.log(`Processing voice result for field: ${fieldId}, transcript: ${transcript}`);
    console.log('Current formData before update:', formData);
    
    const questions = getQuestions(userName);
    const currentQuestion = questions.find(q => q.id === fieldId);
    console.log('Found question:', currentQuestion);
    
    if (currentQuestion?.type === 'select') {
      // For select fields, try to match the transcribed text to an option
      if (fieldId === 'country') {
        const cleanedTranscript = transcript.trim().toLowerCase();
        const matchedCountry = countryOptions.find(option => {
          const cleanedOption = option.label.toLowerCase();
          return cleanedOption.includes(cleanedTranscript) || 
                 cleanedTranscript.includes(cleanedOption) ||
                 cleanedOption.split(' ').some(word => cleanedTranscript.includes(word)) ||
                 cleanedTranscript.split(' ').some(word => cleanedOption.includes(word));
        });
        
        if (matchedCountry) {
          setFormData(prev => ({
            ...prev,
            [fieldId]: matchedCountry.value
          }));
          setShowTip(true);
          setTimeout(() => setShowTip(false), 2000);
        } else {
          setAlertMessage(`Could not match "${transcript}" to a country. Please try again or select manually.`);
          setShowAlert(true);
        }
      } else if (fieldId === 'relationshipType') {
        const cleanedTranscript = transcript.trim().toLowerCase();
        const matchedRelationship = relationshipOptions.find(option => {
          const cleanedOption = option.label.toLowerCase();
          return cleanedOption.includes(cleanedTranscript) || 
                 cleanedTranscript.includes(cleanedOption) ||
                 (cleanedTranscript.includes('friend') && option.value === 'friendship') ||
                 (cleanedTranscript.includes('date') && option.value === 'dating') ||
                 (cleanedTranscript.includes('serious') && option.value === 'serious_relationship') ||
                 (cleanedTranscript.includes('casual') && option.value === 'casual') ||
                 (cleanedTranscript.includes('work') && option.value === 'networking') ||
                 (cleanedTranscript.includes('network') && option.value === 'networking') ||
                 (cleanedTranscript.includes('anything') && option.value === 'any') ||
                 (cleanedTranscript.includes('open') && option.value === 'any');
        });
        
        if (matchedRelationship) {
          setFormData(prev => ({
            ...prev,
            [fieldId]: matchedRelationship.value
          }));
          setShowTip(true);
          setTimeout(() => setShowTip(false), 2000);
        } else {
          setAlertMessage(`Could not match "${transcript}" to a relationship type. Please try again or select manually.`);
          setShowAlert(true);
        }
      }
    } else if (fieldId === 'age') {
      const numbers = transcript.match(/\d+/);
      if (numbers && numbers[0]) {
        const age = parseInt(numbers[0]);
        if (age >= 13 && age <= 120) {
          setFormData(prev => ({
            ...prev,
            [fieldId]: age.toString()
          }));
          setShowTip(true);
          setTimeout(() => setShowTip(false), 2000);
        } else {
          setAlertMessage(`Age ${age} is not valid. Please enter an age between 13 and 120.`);
          setShowAlert(true);
        }
      } else {
        setAlertMessage(`Could not extract age from "${transcript}". Please try again.`);
        setShowAlert(true);
      }
    } else {
      // For text fields, directly use the transcript
      console.log(`Updating text field ${fieldId} with value: ${transcript.trim()}`);
      setFormData(prev => {
        const newData = {
          ...prev,
          [fieldId]: transcript.trim()
        };
        console.log('New formData after update:', newData);
        return newData;
      });
      setShowTip(true);
      setTimeout(() => setShowTip(false), 2000);
    }
  };





  // Start voice recording - simplified
  const startVoiceRecording = (fieldId) => {
    if (isRecording) {
      console.log("Already recording, ignoring request");
      return;
    }

    if (!recognitionRef.current) {
      setAlertMessage("Voice recognition is not supported in your browser.");
      setShowAlert(true);
      return;
    }

    console.log(`Starting voice recording for field: ${fieldId}`);
    
    // Stop any ongoing speech before starting recording
    if (speechSynthRef.current && speechSynthRef.current.speaking) {
      speechSynthRef.current.cancel();
    }
    
    // Set both state and ref to ensure consistency
    setCurrentRecordingField(fieldId);
    currentRecordingFieldRef.current = fieldId;
    isListeningRef.current = true;
    
    try {
      recognitionRef.current.start();
    } catch (error) {
      console.error("Error starting voice recognition:", error);
      setAlertMessage("Could not start voice recognition. Please try again.");
      setShowAlert(true);
      setCurrentRecordingField(null);
      currentRecordingFieldRef.current = null;
      isListeningRef.current = false;
    }
  };

  // Stop voice recording - simplified
  const stopVoiceRecording = () => {
    if (!isRecording || !recognitionRef.current) return;
    
    console.log("Stopping voice recording");
    isListeningRef.current = false;
    
    try {
      recognitionRef.current.stop();
    } catch (error) {
      console.error("Error stopping voice recognition:", error);
    }
  };

  // Text-to-speech function
  const speakText = (text) => {
    if (!isSpeechEnabled || !speechSynthRef.current || !('speechSynthesis' in window)) {
      return;
    }

    if (isRecording) {
      console.log("Recording active, skipping speech");
      return;
    }

    try {
      if (speechSynthRef.current.speaking) {
        speechSynthRef.current.cancel();
        setTimeout(() => {
          if (!isRecording) {
            startSpeech(text);
          }
        }, 150);
      } else {
        startSpeech(text);
      }
    } catch (error) {
      console.log('Speech synthesis setup error:', error);
      setAvatarState(AVATAR_STATES.WAITING);
    }
  };

  const startSpeech = (text) => {
    if (isRecording) {
      console.log("Recording active, not starting speech");
      return;
    }

    try {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.9;
      utterance.pitch = 1.1;
      utterance.volume = 0.7;
      
      const setVoice = () => {
        try {
          const voices = speechSynthRef.current.getVoices();
          if (voices.length > 0) {
            const preferredVoice = voices.find(voice => 
              voice.name.toLowerCase().includes('zira') ||
              voice.name.toLowerCase().includes('susan') ||
              voice.name.toLowerCase().includes('samantha') ||
              voice.name.toLowerCase().includes('karen') ||
              voice.name.toLowerCase().includes('female')
            );
            
            if (preferredVoice) {
              utterance.voice = preferredVoice;
            } else {
              utterance.voice = voices[0];
            }
          }
        } catch (voiceError) {
          console.log('Voice selection error:', voiceError);
        }
      };
      
      setVoice();
      
      if (speechSynthRef.current.getVoices().length === 0) {
        const voicesChangedHandler = () => {
          setVoice();
          speechSynthRef.current.removeEventListener('voiceschanged', voicesChangedHandler);
        };
        speechSynthRef.current.addEventListener('voiceschanged', voicesChangedHandler);
      }
      
      utterance.onstart = () => {
        if (!isRecording) {
          setAvatarState(AVATAR_STATES.TALKING);
        }
      };
      
      utterance.onend = () => {
        if (!isRecording) {
          setAvatarState(AVATAR_STATES.WAITING);
        }
      };
      
      utterance.onerror = (event) => {
        if (event.error !== 'interrupted') {
          console.log('Speech synthesis error:', event.error);
        }
        
        if (!isRecording) {
          setAvatarState(AVATAR_STATES.WAITING);
        }
      };
      
      if (speechSynthRef.current && !speechSynthRef.current.speaking && !isRecording) {
        speechSynthRef.current.speak(utterance);
      }
    } catch (error) {
      console.log('Speech utterance creation error:', error);
      if (!isRecording) {
        setAvatarState(AVATAR_STATES.WAITING);
      }
    }
  };

  // Typing animation for questions
  useEffect(() => {
    const questions = getQuestions(userName);
    if (currentQuestionIndex < questions.length) {
      const question = questions[currentQuestionIndex].question;
      setIsTyping(true);
      setAvatarState(AVATAR_STATES.THINKING);
      setDisplayedQuestion("");
      
      setTimeout(() => {
        speakText(question);
      }, 200);
      
      let i = 0;
      const typeInterval = setInterval(() => {
        setDisplayedQuestion(question.slice(0, i + 1));
        i++;
        
        if (i >= question.length) {
          clearInterval(typeInterval);
          setIsTyping(false);
          
          setTimeout(() => {
            setShowTip(true);
            setAvatarState(AVATAR_STATES.HAPPY);
          }, 1000);
          
          setTimeout(() => {
            if (inputRef.current) {
              inputRef.current.focus();
            }
          }, 1200);
        }
      }, 50);
      
      return () => {
        clearInterval(typeInterval);
        if (speechSynthRef.current) {
          speechSynthRef.current.cancel();
        }
      };
    }
  }, [currentQuestionIndex, userName]);

  // Avatar animations
  const triggerAvatarAnimation = (state) => {
    setAvatarState(state);
    if (avatarRef.current) {
      avatarRef.current.classList.add('avatar-bounce');
      setTimeout(() => {
        avatarRef.current?.classList.remove('avatar-bounce');
      }, 1000);
    }
  };

  // Validation
  const validateAge = (age) => {
    const ageNum = parseInt(age);
    if (isNaN(ageNum) || ageNum < 13 || ageNum > 120) {
      return "Age must be between 13 and 120";
    }
    return "";
  };

  const validateCurrentAnswer = () => {
    const questions = getQuestions(userName);
    const question = questions[currentQuestionIndex];
    const value = formData[question.id];
    
    switch (question.id) {
      case "name":
        return !value.trim() ? "Please enter your name" : "";
      case "age":
        return validateAge(value);
      case "country":
        return !value ? "Please select your country" : "";
      case "relationshipType":
        return !value ? "Please select what you're looking for" : "";
      case "interests":
        return !value.trim() ? "Please tell us about your interests" : "";
      default:
        return "";
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (fieldName, selectedOption) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: selectedOption ? selectedOption.value : ""
    }));
  };

  // Move to next question
  const handleNext = () => {
    const error = validateCurrentAnswer();
    if (error) {
      setAlertMessage(error);
      setShowAlert(true);
      triggerAvatarAnimation(AVATAR_STATES.THINKING);
      return;
    }

    const questions = getQuestions(userName);
    const question = questions[currentQuestionIndex];
    setAnsweredQuestions(prev => [...prev, question.id]);

    if (question.id === "name") {
      setUserName(formData.name);
    }

    setShowTip(false);
    triggerAvatarAnimation(AVATAR_STATES.HAPPY);

    if (currentQuestionIndex < questions.length - 1) {
      setTimeout(() => {
        setCurrentQuestionIndex(prev => prev + 1);
      }, 800);
    } else {
      setTimeout(() => {
        handleSubmit();
      }, 800);
    }
  };

  // Handle Enter key
  const handleKeyPress = (e) => {
    const questions = getQuestions(userName);
    const currentQuestion = questions[currentQuestionIndex];
    
    if (e.key === 'Enter' && !isTyping) {
      if (currentQuestion?.type === 'textarea') {
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          handleNext();
        }
      } else {
        e.preventDefault();
        handleNext();
      }
    }
  };

  // Submit form
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setIsCompleted(true);
    setAvatarState(AVATAR_STATES.THINKING);

    try {
              const guestInfoResponse = await fetch("https://soulmegle.com/api/auth/update-guest-info", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username: guestData.username,
          name: formData.name,
          age: parseInt(formData.age),
          country: formData.country,
          relationshipType: formData.relationshipType,
        }),
      });

      if (!guestInfoResponse.ok) {
        const errorData = await guestInfoResponse.json();
        throw new Error(errorData.error || "Failed to save information");
      }

      // Get the cleaned name from the response
      const guestInfoData = await guestInfoResponse.json();
      const cleanedName = guestInfoData.name;

      let processedInterests = [];
      if (formData.interests.trim()) {
        const interestsResponse = await fetch('https://soulmegle.com/api/auth/update-interests', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            username: guestData.username,
            interests: formData.interests, // Send as string to allow comma separation
            isLoggedIn: false
          }),
          credentials: 'include'
        });

        if (interestsResponse.ok) {
          const interestsData = await interestsResponse.json();
          processedInterests = interestsData.interests || [];
        } else {
          // Handle validation errors
          const errorData = await interestsResponse.json();
          throw new Error(errorData.error || "Failed to save interests");
        }
      }

      const updatedGuestData = {
        ...guestData,
        name: cleanedName, // Use the cleaned name from server
        age: parseInt(formData.age),
        country: formData.country,
        relationshipType: formData.relationshipType,
        interests: processedInterests,
        isFreshGuest: false
      };

      setGuestUserData(updatedGuestData);
      localStorage.setItem("guestUser", JSON.stringify(updatedGuestData));
      
      setAvatarState(AVATAR_STATES.HAPPY);
      
      if (endAudioRef.current) {
        endAudioRef.current.volume = 0.4;
        endAudioRef.current.play().catch(e => console.log("Audio play prevented:", e));
      }
      
      setTimeout(() => {
        navigate("/profile-summary", { replace: true });
      }, 1500);
    } catch (error) {
      setAlertMessage(error.message || "An error occurred. Please try again.");
      setShowAlert(true);
      setAvatarState(AVATAR_STATES.THINKING);
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeAlert = () => setShowAlert(false);

  const questions = getQuestions(userName);
  const currentQuestion = questions[currentQuestionIndex];

  // Render answered question in dumble design
  const renderAnsweredQuestion = (questionId, index) => {
    const question = questions.find(q => q.id === questionId);
    if (!question) return null;

    let displayValue = formData[questionId];
    
    // Format display value based on question type
    if (questionId === 'country') {
      displayValue = getCountryLabel(displayValue);
    } else if (questionId === 'relationshipType') {
      displayValue = getRelationshipTypeLabel(displayValue);
    } else if (questionId === 'age') {
      displayValue = `${displayValue} years old`;
    }

    return (
      <motion.div
        key={questionId}
        className="answered-question-dumble"
        initial={{ opacity: 0, y: 50, scale: 0.8 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ 
          duration: 0.6, 
          delay: index * 0.1,
          type: "spring",
          stiffness: 100
        }}
      >
        <div className="dumble-icon">{question.icon}</div>
        <div className="dumble-content">
          <div className="dumble-label">{question.question.split('?')[0]}?</div>
          <div className="dumble-value">{displayValue}</div>
        </div>
      </motion.div>
    );
  };

  // Render input based on question type
  const renderInput = () => {
    if (!currentQuestion || isCompleted) return null;

    const questionId = currentQuestion.id;
    const value = formData[questionId];

    const renderVoiceButton = () => (
      <motion.button
        type="button"
        className={`voice-button ${isRecording && currentRecordingField === questionId ? 'recording' : ''}`}
        onClick={() => {
          if (isRecording && currentRecordingField === questionId) {
            stopVoiceRecording();
          } else {
            startVoiceRecording(questionId);
          }
        }}
        disabled={isRecording}
        whileHover={{ scale: isRecording ? 1 : 1.1 }}
        whileTap={{ scale: 0.95 }}
        title={isRecording ? "Stop recording" : "Start voice input"}
      >
        {isRecording ? (
          <motion.div
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ repeat: Infinity, duration: 1 }}
          >
            🔴
          </motion.div>
        ) : (
          "🎤"
        )}
      </motion.button>
    );

    switch (currentQuestion.type) {
      case "text":
      case "number":
        return (
          <div className="input-with-voice">
            <input
              ref={inputRef}
              type={currentQuestion.type}
              name={questionId}
              value={value}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              placeholder={currentQuestion.placeholder}
              min={currentQuestion.min}
              max={currentQuestion.max}
              className="form-input"
              autoComplete="off"
            />
            {renderVoiceButton()}
          </div>
        );

      case "select":
        const options = questionId === "country" ? countryOptions : relationshipOptions;
        return (
          <div className="input-with-voice">
            <Select
              ref={inputRef}
              value={options.find(option => option.value === value) || null}
              onChange={(selectedOption) => handleSelectChange(questionId, selectedOption)}
              options={options}
              placeholder={currentQuestion.placeholder}
              className="react-select-container"
              classNamePrefix="react-select"
              isSearchable={questionId === "country"}
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  border: state.isFocused ? '2px solid #6616cd' : '2px solid rgba(102, 22, 205, 0.2)',
                  borderRadius: '8px',
                  padding: '2px 4px',
                  boxShadow: state.isFocused ? '0 0 0 3px rgba(102, 22, 205, 0.1)' : 'none',
                  transition: 'all 0.3s ease',
                  minHeight: '44px',
                  '&:hover': {
                    border: '2px solid #6616cd',
                  }
                }),
                placeholder: (provided) => ({
                  ...provided,
                  color: '#999',
                  fontSize: '0.95rem'
                }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isSelected ? '#6616cd' : state.isFocused ? 'rgba(102, 22, 205, 0.1)' : 'white',
                  color: state.isSelected ? 'white' : '#333',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  padding: '8px 12px',
                  '&:hover': {
                    backgroundColor: state.isSelected ? '#6616cd' : 'rgba(102, 22, 205, 0.1)'
                  }
                }),
                menu: (provided) => ({
                  ...provided,
                  borderRadius: '8px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                  border: '1px solid rgba(102, 22, 205, 0.2)',
                  zIndex: 9999
                }),
                menuPortal: (provided) => ({
                  ...provided,
                  zIndex: 9999
                })
              }}
              menuPortalTarget={document.body}
            />
            {renderVoiceButton()}
          </div>
        );

      case "textarea":
        return (
          <div className="input-with-voice">
            <input
              ref={inputRef}
              type="text"
              name={questionId}
              value={value}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              placeholder={currentQuestion.placeholder}
              className="form-input"
              autoComplete="off"
            />
            {renderVoiceButton()}
          </div>
        );

      default:
        return null;
    }
  };

  if (isCompleted) {
    return (
      <div className="cupid-container default-cupid-background">
        <Navbar />
        <audio ref={endAudioRef} src={solEnd} preload="auto" />
        
        <div className="content-container with-avatar">
          <motion.div 
            className="avatar-container fade-up"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <img src={cupidImage} alt="Sol" className="avatar-image avatar-happy" />

          </motion.div>

          <div className="right-content fade-up">
            <div className="greeting">
              Perfect{userName ? `, ${userName}` : ""}! You're all set! 🎉
            </div>
            <div className="instructions">
              Let's find your interests and get you connected!
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="guest-info-container default-cupid-background">
      <Navbar />
      
      <div className="guest-info-content">
        {/* Left Avatar Section */}
        <div className="avatar-section fade-up">
          <motion.div 
            className={`sol-avatar ${avatarState}`}
            ref={avatarRef}
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ duration: 1, type: "spring", stiffness: 100 }}
          >
            <img src={cupidImage} alt="Sol" />
            {showTip && currentQuestion?.tip && (
              <motion.div 
                className="question-tip fade-up"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                {currentQuestion.tip}
              </motion.div>
            )}
          </motion.div>

          {/* Progress Bar */}
          <div className="progress-container">
            <div className="progress-bar">
              <motion.div 
                className="progress-fill"
                initial={{ width: 0 }}
                animate={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
            <div className="progress-text">
              {currentQuestionIndex + 1} of {questions.length}
            </div>
          </div>
        </div>

        {/* Right Content Section */}
        <div className="right-content fade-up">
          {/* Greeting */}
          <div className="greeting">
            Hello {userName || "Guest User"}
          </div>

          {/* Current Question */}
          <div className="instructions">
            {displayedQuestion}
            {isTyping && <span className="typing-cursor">|</span>}
          </div>

          {/* Answered Questions Stack */}
          <AnimatePresence>
            {answeredQuestions.length > 0 && (
              <motion.div 
                className="answered-questions-stack"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.5 }}
              >
                {answeredQuestions.map((questionId, index) => 
                  renderAnsweredQuestion(questionId, index)
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Input Section */}
          {!isCompleted && (
            <div className="input-container">
              {renderInput()}
            </div>
          )}

          {/* Button Section */}
          {!isCompleted && (
            <div className="button-group">
              <motion.button
                type="button"
                className="next-button"
                onClick={handleNext}
                disabled={isTyping || isSubmitting}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                {currentQuestionIndex === questions.length - 1 ? "Complete Profile" : "Next"}
                <span className="button-arrow">→</span>
              </motion.button>
            </div>
          )}

          {/* Completion Animation */}
          {isCompleted && (
            <motion.div 
              className="completion-animation"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, type: "spring" }}
            >
              <div className="completion-icon">✨</div>
              <div className="completion-text">
                {isSubmitting ? "Creating your profile..." : "Profile completed!"}
              </div>
              {isSubmitting && (
                <div className="loading-dots">
                  <img src={loadingGif} alt="Loading..." />
                </div>
              )}
            </motion.div>
          )}
        </div>
      </div>

      {/* Audio elements */}
      <audio ref={endAudioRef} preload="auto">
        <source src={solEnd} type="audio/mpeg" />
      </audio>

      {/* Alert */}
      <AnimatePresence>
        {showAlert && (
          <AlertBox
            message={alertMessage}
            onClose={closeAlert}
            type="error"
          />
        )}
      </AnimatePresence>
    </div>
  );
}

export default GuestInfoForm; 